/**
 * CreatorProfileModal — deep creator profile loaded from /api/dsp/creator/{handle}
 * Reusable across Discover and Build Wave.
 */
import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { getCreatorProfile, GENERATION_LABELS } from '../../services/dsp';
import {
  Loader2, MapPin, School, Users, TrendingUp, Star, Zap,
  CheckCircle, AlertCircle,
} from 'lucide-react';

const CARD = '#121212';
const BORDER = '#1E1E1E';
const T = '#00F5FF';
const P = '#FF6BBD';

function Stat({ label, value, color = '#fff' }) {
  return (
    <div className="rounded-xl p-3" style={{ background: '#0A0A0A', border: `1px solid ${BORDER}` }}>
      <p className="text-[9px] text-gray-500 uppercase font-semibold tracking-wider">{label}</p>
      <p className="text-base font-black mt-0.5" style={{ color }}>{value}</p>
    </div>
  );
}

function Chip({ children, color = T }) {
  return (
    <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
      style={{ background: `${color}15`, color, border: `1px solid ${color}30` }}>{children}</span>
  );
}

export default function CreatorProfileModal({ handle, open, onOpenChange, onAddToWave }) {
  const [loading, setLoading] = useState(false);
  const [creator, setCreator] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !handle) return;
    let cancelled = false;
    setLoading(true); setError(null); setCreator(null);
    getCreatorProfile(handle)
      .then(res => { if (!cancelled) setCreator(res.creator); })
      .catch(err => {
        if (cancelled) return;
        const msg = err.response?.data?.detail || err.message || 'Failed to load creator';
        setError(typeof msg === 'string' ? msg : 'Creator not found');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [open, handle]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl text-white" style={{ background: CARD, borderColor: BORDER }} data-testid="creator-profile-modal">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Users className="h-5 w-5" style={{ color: T }} />Creator Profile
          </DialogTitle>
          <DialogDescription className="text-gray-500">
            {handle} — Digital DNA™ intelligence
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="flex flex-col items-center py-12" data-testid="creator-loading">
            <Loader2 className="h-8 w-8 animate-spin" style={{ color: T }} />
            <p className="text-sm text-gray-400 mt-3">Loading Digital DNA…</p>
          </div>
        )}

        {error && !loading && (
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl" data-testid="creator-error">
            <p className="text-sm text-red-400 flex items-center gap-2"><AlertCircle className="h-4 w-4" />{error}</p>
          </div>
        )}

        {creator && !loading && (
          <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0" style={{ background: `${T}18`, border: `1px solid ${T}35` }}>
                <Users className="h-7 w-7" style={{ color: T }} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <h3 className="text-xl font-black text-white">{creator.name}</h3>
                  <span className="text-sm" style={{ color: T }}>{creator.handle}</span>
                </div>
                <p className="text-xs text-gray-400 mb-2">{creator.bio}</p>
                <div className="flex flex-wrap gap-1.5">
                  {creator.generation_label && <Chip color={T}>{creator.generation_label}</Chip>}
                  {creator.gender && <Chip color="#aaa">{creator.gender}</Chip>}
                  {creator.is_hbcu && <Chip color={P}>HBCU</Chip>}
                </div>
              </div>
            </div>

            {/* Core stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
              <Stat label="Followers" value={(creator.followers || 0).toLocaleString()} color={T} />
              <Stat label="Engagement" value={`${(creator.engagement_rate || 0).toFixed(1)}%`} color="#4ade80" />
              <Stat label="Avg Rating" value={`${(creator.avg_rating || 0).toFixed(1)} ★`} color="#fbbf24" />
              <Stat label="DNA Signals" value={creator.dna_signals || 0} color={P} />
            </div>

            {/* Demographics */}
            <div className="rounded-xl p-4" style={{ background: '#0A0A0A', border: `1px solid ${BORDER}` }}>
              <h4 className="text-[10px] text-gray-500 uppercase font-bold tracking-wider mb-2">Demographics</h4>
              <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-[11px]">
                {creator.location && <div className="flex items-center gap-1.5 text-gray-300"><MapPin className="h-3 w-3 text-gray-500" />{creator.location}</div>}
                {creator.school && <div className="flex items-center gap-1.5 text-gray-300"><School className="h-3 w-3 text-gray-500" />{creator.school}</div>}
                {creator.age_range && <div className="flex items-center gap-1.5 text-gray-300"><Users className="h-3 w-3 text-gray-500" />Age {creator.age_range}</div>}
              </div>
            </div>

            {/* Platforms & Categories */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <div className="rounded-xl p-4" style={{ background: '#0A0A0A', border: `1px solid ${BORDER}` }}>
                <h4 className="text-[10px] text-gray-500 uppercase font-bold tracking-wider mb-2">Platforms</h4>
                <div className="flex flex-wrap gap-1.5">
                  {(creator.platforms || []).map(p => <Chip key={p} color={T}>{p}</Chip>)}
                </div>
              </div>
              <div className="rounded-xl p-4" style={{ background: '#0A0A0A', border: `1px solid ${BORDER}` }}>
                <h4 className="text-[10px] text-gray-500 uppercase font-bold tracking-wider mb-2">Categories</h4>
                <div className="flex flex-wrap gap-1.5">
                  {(creator.categories || []).map(c => <Chip key={c} color={P}>{c}</Chip>)}
                </div>
              </div>
            </div>

            {/* Wave stats */}
            {creator.wave_stats && (
              <div className="rounded-xl p-4" style={{ background: '#0A0A0A', border: `1px solid ${BORDER}` }} data-testid="creator-wave-stats">
                <h4 className="text-[10px] text-gray-500 uppercase font-bold tracking-wider mb-2 flex items-center gap-1"><TrendingUp className="h-3 w-3" style={{ color: T }} />Wave History</h4>
                <div className="grid grid-cols-4 gap-2">
                  <Stat label="Total" value={creator.wave_stats.total_waves || 0} />
                  <Stat label="Completed" value={creator.wave_stats.completed || 0} color="#4ade80" />
                  <Stat label="Pending" value={creator.wave_stats.pending || 0} color="#fbbf24" />
                  <Stat label="Avg $" value={`$${Math.round(creator.wave_stats.avg_payment || 0)}`} color={P} />
                </div>
              </div>
            )}

            {/* DNA preview */}
            {creator.dna_preview && (
              <div className="rounded-xl p-4" style={{ background: '#0A0A0A', border: `1px solid ${T}25` }} data-testid="creator-dna-preview">
                <h4 className="text-[10px] uppercase font-bold tracking-wider mb-2 flex items-center gap-1" style={{ color: T }}>
                  <Zap className="h-3 w-3" />Digital DNA™ Preview
                </h4>
                <p className="text-[11px] text-gray-400 leading-relaxed whitespace-pre-wrap">{creator.dna_preview}</p>
              </div>
            )}

            {/* Cost models + Action */}
            <div className="flex items-center gap-2 pt-2 flex-wrap">
              <div className="flex-1 flex flex-wrap gap-1.5">
                {(creator.cost_models || []).map(cm => (
                  <span key={cm} className="text-[9px] px-2 py-1 rounded-full font-bold uppercase"
                    style={{ background: '#4ade8018', color: '#4ade80', border: '1px solid #4ade8030' }}>
                    <CheckCircle className="h-2.5 w-2.5 inline mr-1" />{cm}
                  </span>
                ))}
              </div>
              {onAddToWave && (
                <button onClick={() => onAddToWave(creator)} data-testid="add-to-wave-btn"
                  className="px-5 py-2.5 rounded-xl text-xs font-bold text-black flex items-center gap-2"
                  style={{ background: T, boxShadow: `0 0 18px ${T}40` }}>
                  <Star className="h-3.5 w-3.5" />Add to Wave
                </button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
